import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './CategoryDetail.css';

function CategoryDetail() {
  const { category } = useParams();
  const decodedCategory = decodeURIComponent(category);
  const navigate = useNavigate();
  const { selectedGroup, setSelectedGroup } = useAuth();

  const [nominees, setNominees] = useState([]);
  const [allNominees, setAllNominees] = useState({});
  const [selected, setSelected] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [deadline, setDeadline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [groupLoading, setGroupLoading] = useState(!selectedGroup);
  const [savedSelection, setSavedSelection] = useState(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    // If no selectedGroup, try to load user's groups
    if (!selectedGroup) {
      loadGroups();
    }
  }, []);

  useEffect(() => {
    if (!groupLoading) {
      loadData();
    }
  }, [decodedCategory, selectedGroup, groupLoading]);

  const loadGroups = async () => {
    try {
      const groups = await api.getMyGroups();
      if (groups.length > 0) {
        setSelectedGroup(groups[0]);
      }
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setGroupLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [nomineesData, deadlineData] = await Promise.all([
        api.getNominees(),
        api.getDeadline()
      ]);

      setAllNominees(nomineesData);
      setNominees(nomineesData[decodedCategory] || []);
      setDeadline(deadlineData);

      if (selectedGroup) {
        const myPredictions = await api.getMyPredictions(selectedGroup._id);
        setPredictions(myPredictions.predictions || {});
        const savedPick = myPredictions.predictions?.[decodedCategory] || null;
        setSelected(savedPick);
        setSavedSelection(savedPick);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selected || !selectedGroup) return;

    setSaving(true);
    setError('');

    try {
      const updatedPredictions = {
        ...predictions,
        [decodedCategory]: selected
      };

      await api.savePredictions(selectedGroup._id, updatedPredictions);
      setPredictions(updatedPredictions);

      // Navigate to next category or back to categories
      const categories = Object.keys(allNominees);
      const currentIndex = categories.indexOf(decodedCategory);
      if (currentIndex < categories.length - 1) {
        navigate(`/categories/${encodeURIComponent(categories[currentIndex + 1])}`);
      } else {
        navigate('/categories');
      }
    } catch (err) {
      setError(err.message || 'Failed to save prediction');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedSelection = selected && selected !== savedSelection;

  const handleNavigate = (path) => {
    if (hasUnsavedSelection && !isPastDeadline) {
      setPendingNavigation(path);
      setShowSavePrompt(true);
    } else {
      navigate(path);
    }
  };

  const handleSaveAndNavigate = async () => {
    setSaving(true);
    setError('');
    try {
      const updatedPredictions = { ...predictions, [decodedCategory]: selected };
      await api.savePredictions(selectedGroup._id, updatedPredictions);
      setPredictions(updatedPredictions);
      setSavedSelection(selected);
      setShowSavePrompt(false);
      navigate(pendingNavigation);
    } catch (err) {
      setError(err.message || 'Failed to save prediction');
      setShowSavePrompt(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardAndNavigate = () => {
    setShowSavePrompt(false);
    navigate(pendingNavigation);
  };

  const tier = nominees[0]?.tier || '1';
  const isPastDeadline = deadline?.isPastDeadline;
  const categories = Object.keys(allNominees);
  const currentIndex = categories.indexOf(decodedCategory);
  const prevCategory = currentIndex > 0 ? categories[currentIndex - 1] : null;
  const nextCategory = currentIndex < categories.length - 1 ? categories[currentIndex + 1] : null;

  if (loading || groupLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!selectedGroup) {
    return (
      <div className="category-detail-page">
        <div className="card">
          <p>Please join or create a group first to make predictions.</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="category-detail-page">
      <button className="back-button" onClick={() => navigate('/categories')}>
        &#8592; Back to Categories
      </button>

      <div className="category-header">
        <h1>{decodedCategory}</h1>
        <span className={`tier-badge tier-${tier}`}>
          Tier {tier} - {tier === '1' ? '1 Point' : tier === '2' ? '2 Points' : '3 Points'}
        </span>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isPastDeadline && (
        <div className="deadline-banner">
          Voting has closed. You can no longer change your predictions.
        </div>
      )}

      <div className="nominees-list">
        {nominees.map(nominee => (
          <button
            key={nominee.nominee}
            className={`nominee-card ${selected === nominee.nominee ? 'selected' : ''}`}
            onClick={() => !isPastDeadline && setSelected(nominee.nominee)}
            disabled={isPastDeadline}
          >
            <div className="nominee-info">
              <span className="nominee-name">{nominee.nominee}</span>
              {nominee.movie !== nominee.nominee && (
                <span className="nominee-movie">{nominee.movie}</span>
              )}
            </div>
            {selected === nominee.nominee && (
              <span className="nominee-check">&#10003;</span>
            )}
          </button>
        ))}
      </div>

      <div className="category-actions">
        <div className="nav-buttons">
          {prevCategory && (
            <button
              className="btn btn-secondary"
              onClick={() => handleNavigate(`/categories/${encodeURIComponent(prevCategory)}`)}
            >
              &#8592; Previous
            </button>
          )}
          {nextCategory && (
            <button
              className="btn btn-secondary"
              onClick={() => handleNavigate(`/categories/${encodeURIComponent(nextCategory)}`)}
            >
              Next &#8594;
            </button>
          )}
        </div>

        {!isPastDeadline && (
          <button
            className="btn btn-primary submit-btn"
            onClick={handleSubmit}
            disabled={!selected || saving}
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </button>
        )}
      </div>

      {showSavePrompt && (
        <div className="modal-overlay" onClick={() => setShowSavePrompt(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Save Selection?</h2>
            <p>You have an unsaved selection. Would you like to save it before leaving?</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleDiscardAndNavigate}
              >
                Discard
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveAndNavigate}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryDetail;
